import { makeAutoObservable, runInAction } from "mobx";
import type { InstanceActionAudit, InstanceActionResult } from "../infrastructure.types";

/**
 * Follows an update, rollback or restart of the container SERVING HQ.
 *
 * The API cannot run those itself — the first step would stop the process
 * doing the work — so it hands them to a helper container and answers at once
 * with `status: 'handed-off'` (packages/api/src/services/infrastructure/
 * self-update). The helper then stops HQ, starts the new one, waits for it to
 * turn healthy and puts the old one back if it does not. This page stays
 * loaded throughout (it is served by the web container, not the API), so it
 * watches:
 *   - `/health` (public): answering or not, and the version that answers;
 *   - the action history of THIS hand-off (every entry the helper writes
 *     carries its `handoffId`): its action row is the outcome, and a restore
 *     row beside it says the old HQ was put back. No other operator's entry
 *     can be read as this update's outcome.
 * Nothing here is inferred from silence: until an outcome row is read the
 * banner says what it has SEEN, and past {@link SELF_UPDATE_GIVE_UP_MS} it says
 * HQ has not come back and where to look.
 */

/** How often to look while an update runs. */
export const SELF_UPDATE_POLL_MS = 3_000;
/**
 * When to stop waiting: past the helper's own worst case, every phase added up
 * rather than overlapped — its start grace, an image pull (10 min), the slot
 * wait and the stop (1 + 5 min), a lost stop's settle or the health wait
 * (5 min each), then a restore: its own create, start and health watch
 * (≤ 5 min) — plus a margin for ordinary calls.
 */
export const SELF_UPDATE_GIVE_UP_MS = 35 * 60 * 1000;
/* The helper's restart (no image pull, no restore) is bounded by the same watch. */

export type SelfUpdatePhase =
  /** HQ still answers (the helper is pulling / checking). */
  | "handed-off"
  /** HQ is not answering — the swap is under way. */
  | "restarting"
  /** An outcome entry was read. */
  | "finished"
  /** No outcome after SELF_UPDATE_GIVE_UP_MS. */
  | "timed-out";

export interface SelfUpdateDeps {
  /** GET /health → its version, or null when HQ does not answer. */
  fetchVersion: () => Promise<string | null>;
  /** The action-history rows of one hand-off. */
  findAudit: (instanceId: string, handoffId: string) => Promise<InstanceActionAudit[]>;
  now?: () => number;
  setInterval?: (fn: () => void, ms: number) => unknown;
  clearInterval?: (handle: unknown) => void;
}

export interface SelfUpdateRun {
  instanceId: string;
  handoffId: string;
  containerName: string;
  action: string;
  helperName: string;
  requestedAt: string;
  /** The version /health reported before the hand-off (null: not read). */
  fromVersion: string | null;
}

/** Outcome statuses as the audit writes them. */
const FINAL_STATUSES = new Set(["success", "failed", "compensated", "compensation-failed"]);

export default class SelfUpdateWatch {
  run: SelfUpdateRun | null = null;
  phase: SelfUpdatePhase = "handed-off";
  /** The version answering /health at the last look (null: not answering). */
  currentVersion: string | null = null;
  /** HQ stopped answering at least once since the hand-off. */
  sawRestart = false;
  /** The outcome rows (the action's own, and any restore of the old HQ). */
  outcome: InstanceActionAudit[] = [];

  private handle: unknown = null;
  private startedAt = 0;
  private busy = false;

  constructor(private deps: SelfUpdateDeps) {
    makeAutoObservable<SelfUpdateWatch, "handle" | "startedAt" | "busy" | "deps">(this, {
      handle: false,
      startedAt: false,
      busy: false,
      deps: false,
    });
  }

  private now(): number {
    return (this.deps.now ?? Date.now)();
  }

  /** Start watching a hand-off the API just confirmed. */
  begin(result: InstanceActionResult, containerName: string, fromVersion: string | null = null): void {
    if (!result.handedOff) return;
    this.stop();
    this.run = {
      instanceId: result.instanceId,
      handoffId: result.handedOff.handoffId,
      containerName,
      action: result.action,
      helperName: result.handedOff.helperName,
      requestedAt: result.handedOff.requestedAt,
      fromVersion,
    };
    this.phase = "handed-off";
    this.currentVersion = fromVersion;
    this.sawRestart = false;
    this.outcome = [];
    this.startedAt = this.now();
    const every = this.deps.setInterval ?? ((fn: () => void, ms: number) => setInterval(fn, ms));
    this.handle = every(() => void this.tick(), SELF_UPDATE_POLL_MS);
  }

  get active(): boolean {
    return this.run !== null && (this.phase === "handed-off" || this.phase === "restarting");
  }

  /** The action's own outcome row (not a restore row), if read. */
  get actionRow(): InstanceActionAudit | null {
    return this.outcome.find((r) => !String(r.action).endsWith("_compensation")) ?? null;
  }

  /** The restore of the old HQ, if the helper had to do one. */
  get restoreRow(): InstanceActionAudit | null {
    return this.outcome.find((r) => String(r.action).endsWith("_compensation")) ?? null;
  }

  get succeeded(): boolean {
    return this.phase === "finished" && this.actionRow?.status === "success";
  }

  /** One look. Public for tests; the interval calls it. */
  async tick(): Promise<void> {
    const run = this.run;
    if (!run || this.busy || !this.active) return;
    this.busy = true;
    try {
      const version = await this.deps.fetchVersion().catch(() => null);
      // Dismissed while the probe was out (the banner closed, or a sign-out's
      // reset): the probe is a raw fetch, so nothing else stops its answer
      // re-marking a watch that is gone.
      if (this.run !== run) return;
      runInAction(() => {
        this.currentVersion = version;
        // The first version seen before HQ went away is the one being replaced.
        if (version !== null && !this.sawRestart && run.fromVersion === null) run.fromVersion = version;
        if (version === null) {
          this.sawRestart = true;
          this.phase = "restarting";
        } else if (this.phase === "restarting") {
          this.phase = "handed-off";
        }
      });
      if (version !== null) {
        const rows = await this.deps
          .findAudit(run.instanceId, run.handoffId)
          .catch(() => [] as InstanceActionAudit[]);
        if (this.run !== run) return;
        const after = rows.filter(
          (r) => r.handoffId === run.handoffId && FINAL_STATUSES.has(String(r.status))
        );
        // The action's own row is the helper's last word; a restore row, when
        // there is one, is written just before it.
        if (after.some((r) => !String(r.action).endsWith("_compensation"))) {
          runInAction(() => {
            this.outcome = after;
            this.phase = "finished";
          });
          this.stop();
          return;
        }
      }
      if (this.now() - this.startedAt > SELF_UPDATE_GIVE_UP_MS) {
        runInAction(() => {
          this.phase = "timed-out";
        });
        this.stop();
      }
    } finally {
      this.busy = false;
    }
  }

  /** Stop polling (the banner stays until dismissed). */
  stop(): void {
    if (this.handle !== null) {
      (this.deps.clearInterval ?? ((h: unknown) => clearInterval(h as ReturnType<typeof setInterval>)))(this.handle);
      this.handle = null;
    }
  }

  dismiss(): void {
    this.stop();
    this.run = null;
    this.outcome = [];
  }
}
